import React, { useState } from 'react';
import 'react-responsive-carousel/lib/styles/carousel.min.css'; // requires a loader
import { Carousel } from 'react-responsive-carousel';
import { PrimaryIconButton, SecondaryIconButton } from '@hz-design-system/web-ui';
import { useI18nContext } from '@ecg-marktplaats/js-react-i18n';

import CarouselImage from './CarouselImage';

import { TListingGallery } from 'types/TListing';

import Classes from './Carousel.scss';
import './ReactCarousel.scss';

type TCarouselProps = {
  imageUrls: TListingGallery['imageUrls'];
  imageSizes?: TListingGallery['media']['imageSizes'];
  onClickItemHandler?: (index: any) => void;
  onChangeHandler?: (index: any) => void;
  index?: number;
  isModal?: boolean;
  alt: string;
  photoObjectFitCover?: boolean;
};

const SET_SLIDER_TEXT_TIMEOUT = 100;

const CarouselWrapper = ({
  imageUrls = [],
  imageSizes,
  onClickItemHandler,
  onChangeHandler,
  alt,
  index = 0,
  isModal = false,
  photoObjectFitCover = false,
}: TCarouselProps) => {
  const { t } = useI18nContext();
  const [sliderAnnouncementKey, setSliderAnnouncementKey] = useState(0);
  const [sliderAnnouncementText, setSliderAnnouncementText] = useState('');
  const [preLoadSiblingImages, setPreLoadSiblingImages] = useState(isModal);
  const ButtonComponent = isModal ? PrimaryIconButton : SecondaryIconButton;
  if (imageUrls.length <= 1 && !isModal) {
    return null;
  }

  const renderArrowPrevHandler = (onClickHandler, hasPrev) => (
    <>
      <div
        className={`${Classes.navigationContainer} ${isModal ? Classes.outsideNavigation : Classes.insideNavigation} ${Classes.navPrev}`}
      >
        {hasPrev && (
          <ButtonComponent
            name="arrow-left"
            ariaLabel={t('gallery.carousel.previous')}
            onClick={(e) => {
              onClickHandler(e);
              setSliderAnnouncementKey((prev) => prev + 1);
              setSliderAnnouncementText('');
              setTimeout(() => {
                setSliderAnnouncementText(t('gallery.carousel.previousImageClick'));
              }, SET_SLIDER_TEXT_TIMEOUT);
            }}
            className={Classes.navigationButtons}
          />
        )}
      </div>
      <div key={sliderAnnouncementKey} aria-live="polite" aria-atomic="true" className={Classes.srOnly}>
        {sliderAnnouncementText}
      </div>
    </>
  );

  const renderArrowNextHandler = (onClickHandler, hasNext) => (
    <div
      className={`${Classes.navigationContainer} ${isModal ? Classes.outsideNavigation : Classes.insideNavigation} ${Classes.navNext}`}
    >
      {hasNext && (
        <ButtonComponent
          name="arrow-right"
          ariaLabel={t('gallery.carousel.next')}
          onClick={(e) => {
            onClickHandler(e);
            setSliderAnnouncementKey((prev) => prev + 1);
            setSliderAnnouncementText('');
            setTimeout(() => {
              setSliderAnnouncementText(t('gallery.carousel.nextImageClick'));
            }, SET_SLIDER_TEXT_TIMEOUT);
          }}
          // @ts-ignore
          onMouseOver={() => setPreLoadSiblingImages(true)}
          className={Classes.navigationButtons}
        />
      )}
    </div>
  );

  const onSwipeStartHandler = () => {
    setPreLoadSiblingImages(true);
  };

  return (
    <Carousel
      showStatus={false}
      showThumbs={false}
      showArrows
      showIndicators={false}
      className={`${Classes.component} ${!isModal ? Classes.pageCarousel : Classes.dialogCarousel} ${photoObjectFitCover ? Classes.coverFit : ''}`}
      onClickItem={onClickItemHandler}
      onChange={onChangeHandler}
      selectedItem={index}
      renderArrowPrev={renderArrowPrevHandler}
      renderArrowNext={renderArrowNextHandler}
      onSwipeStart={onSwipeStartHandler}
      useKeyboardArrows
      preventMovementUntilSwipeScrollTolerance
      swipeScrollTolerance={20}
    >
      {imageUrls.map((imageUrl, imageIndex) => (
        <CarouselImage
          key={imageIndex}
          alt={alt}
          imageUrl={imageUrl}
          imageIndex={imageIndex}
          index={index}
          isModal={isModal}
          imageSizes={imageSizes}
          onEnterKeyPress={onClickItemHandler}
          preLoadSiblingImages={preLoadSiblingImages}
        />
      ))}
    </Carousel>
  );
};

export default CarouselWrapper;
